import { useOutletContext } from "react-router-dom";
import { HeartPulse, ShieldCheck, Users, Award, Target, Compass } from "lucide-react";
import { HeroFade, FadeUp, StaggerGrid, StaggerItem } from "@/lib/motion";
import { useSEO } from "@/hooks/useSEO";

export default function About() {
  const { site } = useOutletContext();

  useSEO({
    title: "About Us - 25+ Years of Care",
    description: "Learn about the mission, vision, and core medical standards at Afzal Medical Complex. For over two decades, delivering compassionate and advanced healthcare."
  });

  return (
    <div data-testid="about-page">
      <section className="section bg-red-50/40">
        <div className="container-medi grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <HeroFade>
              <div className="overline mb-3">About Us</div>
            </HeroFade>
            <HeroFade delay={0.1}>
              <h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-slate-900">{site?.about_title || "About Afzal Medical Complex"}</h1>
            </HeroFade>
            <HeroFade delay={0.2}>
              <p className="mt-6 text-slate-600 leading-relaxed text-lg">
                {site?.about_body || "Operating as a dedicated trust, Afzal Medical Complex was founded on the vision of our CEO to make advanced medical care accessible to everyone. For over two decades, we have been delivering state-of-the-art treatments and utilizing advanced equipment—all while maintaining the lowest possible prices for our community."}
              </p>
            </HeroFade>
            <StaggerGrid className="mt-8 grid grid-cols-3 gap-4">
              {[
                { icon: Award, n: "25+", l: "Years" },
                { icon: Users, n: "50+", l: "Doctors" },
                { icon: HeartPulse, n: "100K+", l: "Patients" },
              ].map(({ icon: Ico, n, l }) => (
                <StaggerItem key={l} className="card-medi p-4 text-center">
                  <Ico className="w-5 h-5 text-red-700 mx-auto mb-2" />
                  <div className="font-heading font-semibold text-slate-900">{n}</div>
                  <div className="text-xs text-slate-500">{l}</div>
                </StaggerItem>
              ))}
            </StaggerGrid>
          </div>
          <HeroFade delay={0.3}>
            <img src="/ceo.png"
                 alt="Managing Director / CEO"
                 className="rounded-3xl shadow-medi-lg w-full aspect-[4/5] object-cover max-h-[560px]" />
          </HeroFade>
        </div>
      </section>

      <section className="section">
        <StaggerGrid className="container-medi grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Our Mission", desc: site?.mission || "To provide world-class, advanced medical care using state-of-the-art equipment, operating as a trust to ensure the lowest possible prices for our community." },
            { icon: Compass, title: "Our Vision", desc: site?.vision || "To be the most trusted healthcare institution where financial constraints never stand in the way of receiving advanced, life-saving medical treatment." },
            { icon: ShieldCheck, title: "Our Values", desc: "Compassion, affordability, technological excellence, and unwavering integrity guide every decision we make." },
          ].map(({ icon: Ico, title, desc }) => (
            <StaggerItem key={title} className="card-medi p-8">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-700 grid place-items-center mb-4">
                <Ico className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-slate-900">{title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
            </StaggerItem>
          ))}
        </StaggerGrid>
      </section>

      <section className="section bg-slate-50/60">
        <div className="container-medi grid md:grid-cols-2 gap-10 items-center">
          <FadeUp>
            <img src="https://images.pexels.com/photos/13176452/pexels-photo-13176452.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Facilities" className="rounded-3xl shadow-medi-md w-full aspect-video object-cover" />
          </FadeUp>
          <FadeUp delay={0.15}>
            <div>
              <div className="overline mb-3">Modern Facilities</div>
              <h2 className="text-3xl font-heading font-semibold text-slate-900">Equipped for every step of your care journey</h2>
              <ul className="mt-5 space-y-3 text-slate-600">
                {[
                  "State-of-the-art diagnostic imaging (CT, MRI, X-Ray, Ultrasound)",
                  "NABL-standard pathology laboratory",
                  "Dedicated operation theatres with modern anaesthesia",
                  "24/7 emergency, in-house pharmacy and ambulance service",
                  "Comfortable inpatient rooms and private ICU suites",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2.5 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
